<template>
    <div class="faq-container">
        <!-- Left Sidebar -->
        <div class="faq-sidebar">
            <div class="sidebar-header">
                <span class="sidebar-title">历史对话</span>
            </div>
            <div class="new-chat-btn">
                <a-button type="primary" block @click="createNewConversation" :loading="creating" :icon="h(PlusOutlined)">
                    新建对话
                </a-button>
            </div>
            <div class="conversation-list">
                <a-spin :spinning="loadingConversations" class="conv-spin">
                <div
                    v-for="conv in conversations"
                    :key="conv.id"
                    class="conversation-item"
                    :class="{ active: conv.id === activeConversationId }"
                    @click="switchConversation(conv.id)"
                >
                    <div class="conv-info">
                        <div class="conv-name">{{ conv.title }}</div>
                        <div class="conv-preview">{{ conv.lastMessage }}</div>
                        <div class="conv-time">{{ conv.createdAt }}</div>
                    </div>
                    <a-button
                        type="text"
                        size="small"
                        class="conv-delete-btn"
                        :icon="h(DeleteOutlined)"
                        @click.stop="deleteConversationById(conv.id)"
                    />
                </div>
                </a-spin>
            </div>
        </div>

        <!-- Main Chat Area -->
        <div class="faq-main">
            <!-- Header -->
            <div class="chat-header">
                <span class="chat-title">{{ activeConversation.title }}</span>
                <div class="chat-actions">
                    <a-button type="text" danger @click="deleteConversation" :loading="deleting" :icon="h(DeleteOutlined)" />
                </div>
            </div>

            <!-- Messages -->
            <div class="chat-messages" ref="messagesRef">
                <div v-if="loadingMessages" class="chat-loading">
                    <a-spin />
                </div>
                <div
                    v-for="msg in activeConversation.messages"
                    :key="msg.id"
                    class="message-row"
                    :class="msg.role"
                >
                    <div v-if="msg.role === 'assistant'" class="message-avatar">
                        <a-avatar :icon="h(RobotOutlined)" style="background-color: #1890ff;" />
                    </div>
                    <div class="message-bubble" :class="msg.role">
                        <div class="message-content">
                            <template v-for="(seg, idx) in parseContent(msg.content)" :key="idx">
                                <span v-if="seg.type === 'text'">{{ seg.value }}</span>
                                <a-tag v-else class="case-tag" @click="showCaseDetail(seg.value)">
                                    {{ seg.value }}
                                </a-tag>
                            </template>
                        </div>
                        <div class="message-time">{{ msg.createdAt }}</div>
                    </div>
                </div>
                <!-- AI 回复加载中 -->
                <div v-if="aiThinking" class="message-row assistant">
                    <div class="message-avatar">
                        <a-avatar :icon="h(RobotOutlined)" style="background-color: #1890ff;" />
                    </div>
                    <div class="message-bubble assistant thinking-bubble">
                        <div class="thinking-dots">
                            <span class="dot"></span>
                            <span class="dot"></span>
                            <span class="dot"></span>
                            <span class="thinking-text">思考中...</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Input Area -->
            <div class="chat-input-area">
                <div class="input-row">
                    <a-textarea
                        v-model:value="inputText"
                        placeholder="请输入您的问题..."
                        :rows="3"
                        :disabled="sending"
                        @keydown.enter.prevent="sendMessage"
                    />
                    <a-button
                        type="primary"
                        class="send-btn"
                        @click="sendMessage"
                        :loading="sending"
                        :disabled="!inputText.trim() || sending"
                        :icon="h(SendOutlined)"
                    >
                        发送
                    </a-button>
                </div>
            </div>
        </div>
    </div>

    <!-- 案例详情弹窗 -->
    <a-modal
        v-model:open="caseModalVisible"
        title="案例详情"
        width="720px"
        :footer="null"
        destroy-on-close
    >
        <a-spin :spinning="caseLoading" class="case-modal-spin">
            <div v-if="caseDetail" class="case-detail">
                <div class="case-detail-header">
                    <span class="case-detail-badge">{{ caseDetail.caseNo || '-' }}</span>
                    <span class="case-detail-no">{{ caseDetail.caseName || '-' }}</span>
                </div>
                <a-descriptions bordered :column="1" size="middle">
                    <a-descriptions-item
                        v-for="(val, key) in caseFields"
                        :key="key"
                        :label="val"
                    >
                        {{ caseDetail[key] || '-' }}
                    </a-descriptions-item>
                </a-descriptions>
            </div>
            <div v-else-if="!caseLoading" class="case-empty">
                未找到案例信息
            </div>
        </a-spin>
    </a-modal>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { h } from 'vue';
import { message } from 'ant-design-vue';
import {
    SendOutlined, PlusOutlined, DeleteOutlined, RobotOutlined,
} from '@ant-design/icons-vue';
import {
    sendChatMessage,
    createConversation,
    getConversationList,
    deleteConversationById as deleteConversationByIdApi,
    getConversationMessages,
    getCaseByCaseNo,
} from '@/api/service';

export default {
    name: 'FaqPage',
    setup() {
        const conversations = ref([]);
        const activeConversationId = ref(null);
        const inputText = ref('');
        const messagesRef = ref(null);
        const creating = ref(false);
        const deleting = ref(false);
        const sending = ref(false);
        const loadingMessages = ref(false);
        const aiThinking = ref(false);
        const loadingConversations = ref(false);
        const conversationCache = reactive({});

        // 案例详情弹窗
        const caseModalVisible = ref(false);
        const caseLoading = ref(false);
        const caseDetail = ref(null);
        const caseFields = {
            caseName: '案例名称',
            keyword: '关键词',
            taxType: '涉及税种',
            industry: '所属行业',
            taxpayerType: '纳税人类型',
            caseBackground: '案例背景',
            caseSource: '案例来源',
            involvedAmount: '涉及金额',
            coreQuestion: '核心问题',
            analysisProcess: '分析过程',
            handlingResult: '处理结果',
            policyBasis: '政策依据',
            status: '状态',
            createdAt: '创建时间',
        };

        // 当前激活的会话（含消息列表）
        const activeConversation = reactive({
            id: null,
            title: '',
            lastMessage: '',
            createdAt: '',
            messages: [],
        });

        const updateActiveConversation = (conv) => {
            activeConversation.id = conv.id;
            activeConversation.title = conv.title || '';
            activeConversation.lastMessage = conv.lastMessage || '';
            activeConversation.createdAt = conv.createdAt || '';
            activeConversation.messages = conv.messages || [];
        };

        // 加载会话列表
        const fetchConversations = async () => {
            loadingConversations.value = true;
            try {
                const response = await getConversationList({ pageNumber: 1, pageSize: 20 });
                const listData = response.data;
                const convList = listData.conversationList || listData.records || [];
                if (Array.isArray(convList)) {
                    conversations.value = convList.map(item => ({
                        id: item.ConversationId || item.id,
                        title: item.Title || item.title || '新对话',
                        lastMessage: item.lastMessage || '',
                        createdAt: item.CreateTime || item.createdAt || item.createTime || '',
                    }));
                }
            } catch (e) {
                // ignore
            }
            loadingConversations.value = false;
        };

        // 加载会话消息
        const fetchMessages = async (conversationId) => {
            if (!conversationId) return;
            loadingMessages.value = true;
            try {
                const response = await getConversationMessages(conversationId, { pageSize: 50 });
                if (response.code === 200 && response.data) {
                    const messageList = response.data.messageList || [];
                    const messages = messageList.map(item => ({
                        id: item.recordId || item.messageId || item.id,
                        role: item.role === 'assistant' ? 'assistant' : 'user',
                        content: item.contents && item.contents.length > 0
                            ? (item.contents[0].text || '')
                            : (item.content || ''),
                        createdAt: item.createdAt || '',
                    }));
                    conversationCache[conversationId] = messages;
                    const conv = conversations.value.find(c => c.id === conversationId);
                    updateActiveConversation({
                        id: conversationId,
                        title: conv ? conv.title : '',
                        lastMessage: conv ? conv.lastMessage : '',
                        createdAt: conv ? conv.createdAt : '',
                        messages: messages,
                    });
                    scrollToBottom();
                }
            } catch (e) {
                // ignore
            }
            loadingMessages.value = false;
        };

        // 切换会话
        const switchConversation = async (id) => {
            if (id === activeConversationId.value) return;
            activeConversationId.value = id;

            // 如果已缓存消息，直接使用
            if (conversationCache[id]) {
                const conv = conversations.value.find(c => c.id === id);
                updateActiveConversation({
                    id: id,
                    title: conv ? conv.title : '',
                    lastMessage: conv ? conv.lastMessage : '',
                    createdAt: conv ? conv.createdAt : '',
                    messages: conversationCache[id],
                });
                scrollToBottom();
            } else {
                updateActiveConversation({
                    id: id,
                    title: '',
                    lastMessage: '',
                    createdAt: '',
                    messages: [],
                });
                await fetchMessages(id);
            }
        };

        // 新建会话
        const createNewConversation = async () => {
            creating.value = true;
            try {
                const response = await createConversation();
                if (response.code === 200 && response.data) {
                    const data = response.data;
                    const convId = data.conversationId || data.ConversationId || data.id;
                    if (!convId) {
                        message.error('创建对话失败：返回数据异常');
                        creating.value = false;
                        return;
                    }
                    const newConv = {
                        id: convId,
                        title: data.Title || data.title || '新对话',
                        lastMessage: data.lastMessage || '',
                        createdAt: data.CreateTime || data.createTime || data.createdAt || '',
                    };
                    conversations.value.unshift(newConv);
                    activeConversationId.value = convId;
                    conversationCache[convId] = [];
                    updateActiveConversation({
                        id: convId,
                        title: newConv.title,
                        lastMessage: '',
                        createdAt: newConv.createdAt,
                        messages: [],
                    });
                }
            } catch (e) {
                message.error('创建对话失败');
            }
            creating.value = false;
        };

        // 删除指定会话（侧边栏用）
        const deleteConversationById = async (id) => {
            if (!id) return;
            deleting.value = true;
            try {
                const response = await deleteConversationByIdApi(id);
                if (response.code === 200) {
                    const isActive = id === activeConversationId.value;
                    const index = conversations.value.findIndex(c => c.id === id);
                    conversations.value = conversations.value.filter(c => c.id !== id);
                    delete conversationCache[id];
                    if (conversations.value.length === 0) {
                        activeConversationId.value = null;
                        updateActiveConversation({ id: null, title: '', lastMessage: '', createdAt: '', messages: [] });
                    } else if (isActive) {
                        const nextIndex = Math.min(index, conversations.value.length - 1);
                        const nextConv = conversations.value[nextIndex];
                        if (nextConv) {
                            activeConversationId.value = nextConv.id;
                            await switchConversation(nextConv.id);
                        }
                    }
                }
            } catch (e) {
                message.error('删除对话失败');
            }
            deleting.value = false;
        };

        // 删除当前会话（右上角用）
        const deleteConversation = async () => {
            if (!activeConversationId.value) return;
            await deleteConversationById(activeConversationId.value);
        };

        // 发送消息
        const sendMessage = async () => {
            if (sending.value) return;
            const text = inputText.value.trim();
            if (!text) return;

            if (!activeConversationId.value) {
                message.warning('请先创建对话');
                return;
            }

            sending.value = true;
            aiThinking.value = true;
            const userMsg = {
                id: 'temp-' + Date.now(),
                role: 'user',
                content: text,
                createdAt: new Date().toLocaleTimeString('zh-CN', { hour12: false }),
            };

            // 本地先添加用户消息（activeConversation.messages 就是 conversationCache 的引用）
            activeConversation.messages.push(userMsg);
            scrollToBottom();

            try {
                const response = await sendChatMessage({
                    conversationId: activeConversationId.value,
                    message: text,
                });
                if (response.code === 200 && response.data) {
                    const aiMsg = {
                        id: response.data.id || 'ai-' + Date.now(),
                        role: 'assistant',
                        content: response.data.reply || '',
                        createdAt: new Date().toLocaleTimeString('zh-CN', { hour12: false }),
                    };
                    activeConversation.messages.push(aiMsg);
                    // 更新会话列表的摘要
                    const conv = conversations.value.find(c => c.id === activeConversationId.value);
                    if (conv) {
                        conv.lastMessage = text;
                    }
                    scrollToBottom();
                }
            } catch (e) {
                message.error('发送失败');
            }
            aiThinking.value = false;
            sending.value = false;
            inputText.value = '';
        };

        const scrollToBottom = () => {
            setTimeout(() => {
                if (messagesRef.value) {
                    messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
                }
            }, 50);
        };

        // 解析消息内容，将【【xxx】】转换为可点击标签
        const parseContent = (content) => {
            if (!content) return [{ type: 'text', value: '' }];
            const segments = [];
            const regex = /【【(.+?)】】/g;
            let lastIndex = 0;
            let match;
            while ((match = regex.exec(content)) !== null) {
                if (match.index > lastIndex) {
                    segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
                }
                segments.push({ type: 'case', value: match[1] });
                lastIndex = match.index + match[0].length;
            }
            if (lastIndex < content.length) {
                segments.push({ type: 'text', value: content.slice(lastIndex) });
            }
            return segments.length > 0 ? segments : [{ type: 'text', value: content }];
        };

        // 点击案例标签，查询详情
        const showCaseDetail = async (caseNo) => {
            caseDetail.value = null;
            caseLoading.value = true;
            caseModalVisible.value = true;
            try {
                const response = await getCaseByCaseNo(caseNo);
                if (response.code === 200) {
                    const data = response.data;
                    // 可能是数组或单个对象
                    caseDetail.value = Array.isArray(data) ? data[0] : data;
                }
            } catch (e) {
                // ignore
            }
            caseLoading.value = false;
        };

        onMounted(() => {
            fetchConversations();
        });

        return {
            conversations,
            activeConversationId,
            activeConversation,
            inputText,
            messagesRef,
            creating,
            deleting,
            sending,
            loadingMessages,
            SendOutlined,
            PlusOutlined,
            DeleteOutlined,
            RobotOutlined,
            h,
            aiThinking,
            loadingConversations,
            deleteConversationById,
            switchConversation,
            createNewConversation,
            deleteConversation,
            sendMessage,
            parseContent,
            showCaseDetail,
            caseModalVisible,
            caseLoading,
            caseDetail,
            caseFields,
        };
    },
};
</script>

<style scoped>
.faq-container {
    display: flex;
    height: calc(100vh - 120px);
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.06);
}

/* ===== Sidebar ===== */
.faq-sidebar {
    width: 280px;
    min-width: 280px;
    background: linear-gradient(180deg, #f8f9fc 0%, #f0f2f5 100%);
    border-right: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;
}

.sidebar-header {
    padding: 24px 20px 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.sidebar-header::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #1890ff;
    border-radius: 50%;
}

.sidebar-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a2e;
    letter-spacing: 0.5px;
}

.new-chat-btn {
    padding: 12px 16px 16px;
}

.new-chat-btn .ant-btn {
    border-radius: 8px;
    height: 40px;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.2);
    transition: all 0.25s ease;
}

.new-chat-btn .ant-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}

.conversation-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 10px 12px;
}

.conv-spin {
    width: 100%;
    min-height: 100px;
}

.conversation-list::-webkit-scrollbar {
    width: 4px;
}

.conversation-list::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
}

.conversation-item {
    padding: 14px 14px;
    border-radius: 10px;
    cursor: pointer;
    margin-bottom: 6px;
    transition: all 0.2s ease;
    position: relative;
    border: 1px solid #e8e8e8;
    background: #fff;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}

.conversation-item:hover .conv-delete-btn {
    opacity: 1;
    pointer-events: auto;
}

.conv-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.conv-delete-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    flex-shrink: 0;
    margin-top: 2px;
    color: #bfbfbf;
}

.conv-delete-btn:hover {
    color: #ff4d4f !important;
}

.conversation-item:hover {
    background: #fff;
    border-color: #e8e8e8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.conversation-item.active {
    background: #fff;
    border-color: #1890ff;
    box-shadow: 0 2px 12px rgba(24, 144, 255, 0.12);
}

.conversation-item.active::before {
    content: '';
    position: absolute;
    left: -11px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 32px;
    background: #1890ff;
    border-radius: 0 3px 3px 0;
}

.conv-name {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.conv-preview {
    font-size: 12px;
    color: #8c8c8c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px;
    line-height: 1.4;
}

.conv-time {
    font-size: 11px;
    color: #bfbfbf;
}

/* ===== Main Area ===== */
.faq-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f5f6fa;
}

.chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 28px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid #e8e8e8;
    flex-shrink: 0;
}

.chat-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
    position: relative;
    padding-left: 16px;
}

.chat-title::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 18px;
    background: linear-gradient(180deg, #1890ff, #40a9ff);
    border-radius: 2px;
}

.chat-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.chat-actions .ant-btn-text:hover {
    background: rgba(255, 77, 79, 0.08);
}

/* ===== Messages ===== */
.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 28px 32px;
    scroll-behavior: smooth;
}

.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
}

.chat-loading {
    display: flex;
    justify-content: center;
    padding: 60px 0;
}

.message-row {
    display: flex;
    margin-bottom: 24px;
    align-items: flex-start;
}

.message-row.user {
    justify-content: flex-end;
}

.message-avatar {
    margin-right: 14px;
    flex-shrink: 0;
    margin-top: 4px;
}

.message-bubble {
    max-width: 68%;
    padding: 14px 20px;
    border-radius: 14px;
    position: relative;
    word-break: break-word;
    line-height: 1.7;
    font-size: 14px;
}

.message-bubble.user {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 12px rgba(24, 144, 255, 0.2);
}

.message-bubble.assistant {
    background: #fff;
    color: #1a1a2e;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
}

.message-bubble.assistant .message-content {
    white-space: pre-wrap;
}

/* Thinking / Loading indicator */
.thinking-bubble {
    min-width: 120px;
}

.thinking-dots {
    display: flex;
    align-items: center;
    gap: 6px;
}

.thinking-text {
    font-size: 13px;
    color: #8c8c8c;
    margin-left: 4px;
}

.dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1890ff;
    animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}

.dot:nth-child(3) {
    animation-delay: 0s;
}

@keyframes bounce {
    0%, 80%, 100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.message-content {
    line-height: 1.7;
}

.message-time {
    font-size: 11px;
    margin-top: 8px;
    opacity: 0.7;
    text-align: right;
    letter-spacing: 0.3px;
}

.message-bubble.user .message-time {
    color: rgba(255, 255, 255, 0.75);
}

.message-bubble.assistant .message-time {
    color: #bfbfbf;
}

/* ===== Input Area ===== */
.chat-input-area {
    padding: 16px 28px 20px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    flex-shrink: 0;
}

.input-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.input-row .ant-textarea {
    flex: 1;
    border-radius: 10px;
    border-color: #d9d9d9;
    resize: none;
    font-size: 14px;
    padding: 10px 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.input-row .ant-textarea:hover,
.input-row .ant-textarea:focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.06);
}

.send-btn {
    height: 78px;
    min-width: 92px;
    border-radius: 10px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
    transition: all 0.25s ease;
}

.send-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.3);
}

/* ===== 案例标签 ===== */
.case-tag {
    cursor: pointer;
    font-weight: 700;
    font-size: 12px;
    padding: 3px 12px 3px 10px;
    border-radius: 6px;
    background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
    border: 1.5px solid #69c0ff;
    color: #0050b3;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    display: inline-block;
    margin: 2px 3px;
    line-height: 1.8;
    position: relative;
    letter-spacing: 0.3px;
}

.case-tag::before {
    content: '📎';
    font-size: 11px;
    margin-right: 4px;
    display: inline-block;
}

.case-tag:hover {
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    color: #fff;
    border-color: #096dd9;
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 4px 14px rgba(24, 144, 255, 0.4);
}

.case-tag:active {
    transform: translateY(0) scale(0.97);
}

/* ===== 案例详情弹窗 ===== */
.case-modal-spin {
    min-height: 200px;
}

.case-detail-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.case-detail-badge {
    display: inline-block;
    padding: 4px 12px;
    background: #e6f7ff;
    color: #1890ff;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.case-detail-no {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a2e;
}

.case-empty {
    text-align: center;
    padding: 60px 0;
    color: #8c8c8c;
    font-size: 14px;
}

.case-detail .ant-descriptions-item-label {
    font-weight: 600;
    color: #595959;
    width: 120px;
    white-space: nowrap;
    background: #fafafa;
}

.case-detail .ant-descriptions-item-content {
    color: #1a1a2e;
}
</style>
